import { Head, router, useForm, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { PageHeader } from "@/components/layouts/PageHeader"
import { TIMEZONE_OPTIONS } from "@/lib/date"
import { Eye, EyeOff, Github } from "lucide-react"
import { useState } from "react"
import type { FormEvent } from "react"
import type { SharedPageProps } from "@/types"

interface GithubRepo {
  full_name: string
  name: string
  private: boolean
}

interface GithubSettings {
  connected: boolean
  connected_at: string | null
  login: string | null
  repos: GithubRepo[]
  repos_error: string | null
}

interface SettingsProps {
  settings: {
    outbound_webhook_url: string | null
    outbound_webhook_api_key: string | null
    github_repo: string | null
  }
  github: GithubSettings
}

function WebhookSettingsCard({ settings }: { settings: SettingsProps["settings"] }) {
  const [showApiKey, setShowApiKey] = useState(false)

  const { data, setData, patch, processing, errors } = useForm({
    outbound_webhook_url: settings.outbound_webhook_url || "",
    outbound_webhook_api_key: settings.outbound_webhook_api_key || "",
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch("/admin/settings")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Outbound Webhook</CardTitle>
        <CardDescription>
          Configure where Feedwatch delivers capture payloads.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="outbound_webhook_url">Webhook URL</Label>
            <Input
              id="outbound_webhook_url"
              type="url"
              placeholder="https://example.com/api/intake"
              value={data.outbound_webhook_url}
              onChange={(e) => setData("outbound_webhook_url", e.target.value)}
            />
            {errors.outbound_webhook_url && (
              <p className="text-destructive text-sm">{errors.outbound_webhook_url}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="outbound_webhook_api_key">API Key</Label>
            <div className="relative">
              <Input
                id="outbound_webhook_api_key"
                type={showApiKey ? "text" : "password"}
                placeholder="Bearer token for authorization header"
                value={data.outbound_webhook_api_key}
                onChange={(e) => setData("outbound_webhook_api_key", e.target.value)}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </Button>
            </div>
            {errors.outbound_webhook_api_key && (
              <p className="text-destructive text-sm">{errors.outbound_webhook_api_key}</p>
            )}
          </div>

          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : "Save Settings"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function GithubDestinationCard({
  github,
  selectedRepo,
}: {
  github: GithubSettings
  selectedRepo: string | null
}) {
  const [showToken, setShowToken] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [savingRepo, setSavingRepo] = useState(false)

  const connectForm = useForm({
    github_token: "",
  })

  const repoForm = useForm({
    github_repo: selectedRepo || "",
  })

  function handleConnect(e: FormEvent) {
    e.preventDefault()
    connectForm.post("/admin/settings/github_connect")
  }

  function persistRepo(repo: string) {
    if (!repo.trim()) return

    repoForm.setData("github_repo", repo)
    setSavingRepo(true)
    router.patch(
      "/admin/settings/update_github_repo",
      { github_repo: repo },
      {
        preserveScroll: true,
        onFinish: () => setSavingRepo(false),
      }
    )
  }

  function handleRepoChange(value: string) {
    persistRepo(value)
  }

  function handleRepoSave() {
    persistRepo(repoForm.data.github_repo)
  }

  function handleDisconnect() {
    router.delete("/admin/settings/github_disconnect", {
      onFinish: () => setDisconnectOpen(false),
    })
  }

  const repoNames = github.repos.map((repo) => repo.full_name)
  if (selectedRepo && !repoNames.includes(selectedRepo)) {
    repoNames.unshift(selectedRepo)
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
            <Github className="size-5" />
          </div>
          <div className="flex-1">
            <CardTitle>GitHub Destination</CardTitle>
            <CardDescription>
              Sources can commit capture markdown into this private repository.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {github.connected ? (
          <>
            <div className="bg-muted space-y-1 rounded-md p-3 text-sm">
              {github.login && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Account:</span>
                  <span>{github.login}</span>
                </div>
              )}
              {github.connected_at && (
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Connected:</span>
                  <span>{new Date(github.connected_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {github.repos_error && (
              <p className="text-destructive text-sm">
                Could not list repositories: {github.repos_error}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="github_repo">Repository</Label>
              <Select
                value={repoForm.data.github_repo || undefined}
                onValueChange={handleRepoChange}
                disabled={savingRepo}
              >
                <SelectTrigger id="github_repo" className="w-full max-w-lg">
                  <SelectValue placeholder="Select a repository" />
                </SelectTrigger>
                <SelectContent>
                  {repoNames.map((fullName) => {
                    const repo = github.repos.find((item) => item.full_name === fullName)
                    return (
                      <SelectItem key={fullName} value={fullName}>
                        {fullName}
                        {repo?.private ? " (private)" : ""}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                Folder and file name are set on each source. GitHub delivery writes <code>.md</code> files here.
              </p>
              <Button
                type="button"
                onClick={handleRepoSave}
                disabled={savingRepo || !repoForm.data.github_repo}
              >
                {savingRepo ? "Saving..." : "Save Repository"}
              </Button>
            </div>

            <Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  Disconnect
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Disconnect GitHub</DialogTitle>
                  <DialogDescription>
                    Sources set to deliver to GitHub will fail until you connect again and choose a repository.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDisconnectOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleDisconnect}>
                    Disconnect
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <form onSubmit={handleConnect} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="github_token">Fine-grained personal access token</Label>
              <div className="relative">
                <Input
                  id="github_token"
                  type={showToken ? "text" : "password"}
                  placeholder="github_pat_..."
                  value={connectForm.data.github_token}
                  onChange={(e) => connectForm.setData("github_token", e.target.value)}
                  className="pr-10"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute top-1/2 right-1 -translate-y-1/2"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
              <p className="text-muted-foreground text-xs">
                Grant <span className="font-medium">Contents: Read and write</span> on the target repository.{" "}
                <a
                  href="https://github.com/settings/personal-access-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                >
                  Create a token
                </a>
              </p>
            </div>
            <Button type="submit" disabled={connectForm.processing || !connectForm.data.github_token.trim()}>
              {connectForm.processing ? "Connecting..." : "Connect GitHub"}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

function ChangePasswordCard() {
  const { data, setData, patch, processing, errors, reset } = useForm({
    current_password: "",
    password: "",
    password_confirmation: "",
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch("/admin/settings/change_password", {
      onSuccess: () => reset(),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change Password</CardTitle>
        <CardDescription>
          Update your account password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              value={data.current_password}
              onChange={(e) => setData("current_password", e.target.value)}
              autoComplete="current-password"
              required
            />
            {errors.current_password && (
              <p className="text-destructive text-sm">{errors.current_password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <Input
              id="password"
              type="password"
              value={data.password}
              onChange={(e) => setData("password", e.target.value)}
              autoComplete="new-password"
              required
            />
            {errors.password && (
              <p className="text-destructive text-sm">{errors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmation">Confirm New Password</Label>
            <Input
              id="password_confirmation"
              type="password"
              value={data.password_confirmation}
              onChange={(e) => setData("password_confirmation", e.target.value)}
              autoComplete="new-password"
              required
            />
            {errors.password_confirmation && (
              <p className="text-destructive text-sm">{errors.password_confirmation}</p>
            )}
          </div>

          <Button type="submit" disabled={processing}>
            {processing ? "Updating..." : "Update Password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function TimezoneCard() {
  const { props } = usePage<SharedPageProps>()
  const currentTimezone = props.current_user?.timezone || "America/New_York"

  const { data, setData, patch, processing } = useForm({
    timezone: currentTimezone,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    patch("/admin/settings/update_timezone")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Timezone</CardTitle>
        <CardDescription>
          All dates and times will be displayed in this timezone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={data.timezone}
              onValueChange={(value) => setData("timezone", value)}
            >
              <SelectTrigger id="timezone" className="w-[300px]">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" disabled={processing}>
            {processing ? "Saving..." : "Save Timezone"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

export default function Settings({ settings, github }: SettingsProps) {
  return (
    <>
      <Head title="Settings" />
      <div className="space-y-6">
        <PageHeader title="Settings" />
        <TimezoneCard />
        <WebhookSettingsCard settings={settings} />
        <GithubDestinationCard github={github} selectedRepo={settings.github_repo} />
        <ChangePasswordCard />
      </div>
    </>
  )
}
